import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AppointmentMap from '@/components/AppointmentMap';
import ChatWidget from '@/components/ChatWidget';
import HotelBookingSection from '@/components/HotelBookingSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Bell, Check, CreditCard, Mail, Smartphone,
  Loader2, ExternalLink, Shield, Lock, Zap,
  Activity, Eye, EyeOff, Sparkles, TrendingUp, Clock,
  AlertTriangle, X, RefreshCw, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// DHMAD.tn (tunesischer Escrow/"séquestre"-Dienst) — alternative Zahlungsmethode
// für tunesische Nutzer, die keine Kreditkarte für Stripe haben. Der ECHTE Link
// wird dynamisch von GET /api/payment/dhmad-link geladen (siehe useEffect unten,
// vom dhmad-automation-Skript nach jeder Zahlung neu gesetzt) — das hier ist nur
// der Fallback, falls die Abfrage fehlschlägt oder noch nie ein Link gesetzt wurde.
const DHMAD_PAYMENT_URL = 'https://dhmad.tn/';

// Country phone codes mapping
const countryPhoneCodes = {
  'Tunisia': '+216',
  'Morocco': '+212',
  'UK': '+44',
  'Ireland': '+353',
  'Algeria': '+213',
  'Egypt': '+20',
  'Nigeria': '+234',
  'Thailand': '+66',
  'Iraq': '+964',
  'Cameroon': '+237',
  'Tanzania': '+255',
  'UAE': '+971',
  'Qatar': '+974',
  'China': '+86',
  'India': '+91'
};

// Visa Type options (French / English)
const VISA_TYPES = [
  { value: 'Tourism', fr: 'Tourisme', en: 'Tourism' },
  { value: 'Business', fr: 'Affaires', en: 'Business' },
  { value: 'Family or Private Visit', fr: 'Visite familiale ou privée', en: 'Family or Private Visit' },
  { value: 'Student', fr: 'Études', en: 'Student' },
  { value: 'Work', fr: 'Travail', en: 'Work' },
  { value: 'Family Reunification', fr: 'Regroupement familial', en: 'Family Reunification' },
  { value: 'Medical Treatment', fr: 'Soins médicaux', en: 'Medical Treatment' },
  { value: 'Airport Transit', fr: 'Transit aéroportuaire', en: 'Airport Transit' },
  { value: 'Long-Stay', fr: 'Long séjour', en: 'Long-Stay' },
  { value: 'Cultural Exchange', fr: 'Échange culturel', en: 'Cultural Exchange' },
  { value: 'Sports', fr: 'Activité sportive', en: 'Sports' },
  { value: 'Conference / Seminar', fr: 'Conférence / Séminaire', en: 'Conference / Seminar' },
  { value: 'Official Visit', fr: 'Visite officielle', en: 'Official Visit' },
  { value: 'Training', fr: 'Formation', en: 'Training' },
  { value: 'Scientific Research', fr: 'Recherche scientifique', en: 'Scientific Research' },
  { value: 'Internship', fr: 'Stage', en: 'Internship' },
  { value: 'Au Pair', fr: 'Au pair', en: 'Au Pair' },
  { value: 'Volunteering', fr: 'Volontariat', en: 'Volunteering' },
];

const Dashboard = () => {
  const { t } = useLanguage();
  const { user, isSubscribed, updateUser } = useAuth();
  const navigate = useNavigate();

  const [preferences, setPreferences] = useState({
    notification_type: 'email',
    residence_country: 'Tunisia',
    destination_country: 'France',
    visa_center: 'TLS',
    visa_type: 'Tourism',
    phone_number: ''
  });
  const [countries, setCountries] = useState({ from_countries: [], to_countries: [], visa_centers: [] });
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [alertsThisWeek, setAlertsThisWeek] = useState(0);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [alertsToday, setAlertsToday] = useState(47);
  const [showPopup, setShowPopup] = useState(false);
  const [popupAlert, setPopupAlert] = useState(null);
  const [dhmadLink, setDhmadLink] = useState(DHMAD_PAYMENT_URL);

  // Aktuellen (frisch generierten) DHMAD-Zahlungslink holen — wird vom
  // Automatisierungsskript nach jeder abgeschlossenen Zahlung aktualisiert.
  useEffect(() => {
    const fetchDhmadLink = async () => {
      try {
        const response = await axios.get(`${API}/payment/dhmad-link`);
        if (response.data?.url) {
          setDhmadLink(response.data.url);
        }
      } catch (error) {
        // Fällt auf DHMAD_PAYMENT_URL zurück, kein Problem für den Nutzer.
      }
    };
    fetchDhmadLink();
  }, []);

  // Fetch alerts this week for personal counter
  useEffect(() => {
    const fetchAlertsThisWeek = async () => {
      try {
        const country = preferences.residence_country || 'Tunisia';
        const response = await axios.get(`${API}/stats/alerts-this-week?country=${country}`);
        setAlertsThisWeek(response.data.alerts_this_week);
      } catch (error) {
        setAlertsThisWeek(0);
      }
    };
    fetchAlertsThisWeek();
  }, [preferences.residence_country]);

  // Get phone code for current residence country
  const getPhoneCode = () => {
    return countryPhoneCodes[preferences.residence_country] || '+1';
  };

  // Popup for non-subscribed users after 30 seconds
  useEffect(() => {
    if (!isSubscribed) {
      const timer = setTimeout(() => {
        const randomAlert = {
          country: preferences.destination_country || 'France',
          slots: Math.floor(Math.random() * 8) + 2,
          center: preferences.visa_center || 'TLS'
        };
        setPopupAlert(randomAlert);
        setShowPopup(true);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [isSubscribed, preferences.destination_country, preferences.visa_center]);

  // Truly random number generator
  const getTrueRandom = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Generate dynamic mock alerts - truly random every 30 minutes
  const generateMockAlerts = () => {
    const destinations = [
      { country: 'France', cities: ['Paris Embassy', 'Lyon TLS', 'Marseille VFS', 'Nice Consulate'] },
      { country: 'Germany', cities: ['Berlin VFS', 'Munich TLS', 'Frankfurt BLS', 'Hamburg Consulate'] },
      { country: 'Spain', cities: ['Madrid BLS', 'Barcelona TLS', 'Valencia VFS', 'Seville Consulate'] },
      { country: 'Italy', cities: ['Rome TLS', 'Milan VFS', 'Naples BLS', 'Florence Consulate'] },
      { country: 'Netherlands', cities: ['Amsterdam VFS', 'Rotterdam TLS', 'The Hague BLS'] },
      { country: 'Belgium', cities: ['Brussels TLS', 'Antwerp VFS', 'Ghent BLS'] },
      { country: 'Portugal', cities: ['Lisbon VFS', 'Porto TLS', 'Faro BLS'] },
      { country: 'Malta', cities: ['Valletta VFS', 'St. Julians TLS'] },
      { country: 'Hungary', cities: ['Budapest VFS', 'Debrecen TLS'] },
      { country: 'Iceland', cities: ['Reykjavik VFS'] },
      { country: 'Austria', cities: ['Vienna TLS', 'Salzburg VFS'] },
      { country: 'Switzerland', cities: ['Zurich VFS', 'Geneva TLS', 'Bern BLS'] },
    ];

    const colors = [
      'bg-[#FF3B30]', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500',
      'bg-green-500', 'bg-indigo-500', 'bg-pink-500', 'bg-cyan-500',
      'bg-amber-500', 'bg-teal-500', 'bg-rose-500', 'bg-emerald-500'
    ];

    const alerts = [];
    const usedIndices = new Set();

    for (let i = 0; i < 4; i++) {
      let countryIndex;
      do {
        countryIndex = getTrueRandom(0, destinations.length - 1);
      } while (usedIndices.has(countryIndex));
      usedIndices.add(countryIndex);

      const destData = destinations[countryIndex];
      const cityIndex = getTrueRandom(0, destData.cities.length - 1);
      const city = destData.cities[cityIndex];
      const slots = getTrueRandom(2, 15);

      const daysAhead = getTrueRandom(7, 30);
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);
      const dateStr = futureDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      let timeAgo;
      if (i === 0) {
        const mins = getTrueRandom(1, 5);
        timeAgo = { text: 'NEW', badge: true, mins };
      } else {
        const mins = getTrueRandom(5 + (i * 5), 15 + (i * 10));
        timeAgo = { text: `${mins} min ago`, badge: false };
      }

      alerts.push({
        country: destData.country,
        city,
        slots,
        date: dateStr,
        time: timeAgo,
        color: colors[countryIndex % colors.length]
      });
    }

    return alerts;
  };

  // Generate alerts today count - truly random
  const generateAlertsToday = () => {
    return getTrueRandom(35, 85);
  };

  // Update mock data every 30 minutes with TRUE randomness
  useEffect(() => {
    const updateMockData = () => {
      setLiveAlerts(generateMockAlerts());
      setAlertsToday(generateAlertsToday());
    };

    updateMockData();

    const interval = setInterval(() => {
      updateMockData();
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prefsRes, countriesRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/preferences`),
        axios.get(`${API}/countries`),
        axios.get(`${API}/appointments/latest`).catch(() => ({ data: [] }))
      ]);

      if (prefsRes.data) {
        setPreferences(prev => ({ ...prev, ...prefsRes.data }));
      }
      setCountries(countriesRes.data);
      setAppointments(appointmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ersetzt {platzhalter} in einem übersetzten Text durch fett dargestellte
  // React-Knoten (z.B. Städtenamen), damit die <strong>-Hervorhebung auch in
  // übersetzten (en/fr) Strings erhalten bleibt.
  const interpolateBold = (template, replacements) => {
    const keys = Object.keys(replacements).join('|');
    const parts = template.split(new RegExp(`\\{(${keys})\\}`, 'g'));
    return parts.map((part, i) =>
      replacements[part] !== undefined
        ? <strong key={i} className="text-white">{replacements[part]}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/preferences`, preferences);
      toast.success('Preferences saved successfully!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A0A0A]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="dashboard-page">
      <Navbar />

      {/* Popup for non-subscribed users */}
      {showPopup && !isSubscribed && popupAlert && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-300">
            <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B5B] p-4 rounded-t-xl relative">
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-3 right-3 text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">{t('popup_just_now')}</p>
                  <p className="text-white font-bold text-lg">{t('popup_title')}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium text-center">
                  {t('popup_slots').replace('{count}', popupAlert.slots).replace('{country}', popupAlert.country).replace('{center}', popupAlert.center)}
                </p>
              </div>

              <p className="text-gray-600 text-center mb-4">
                {t('popup_subscribe_text')}
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 justify-center">
                  <RefreshCw className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 text-sm font-medium">
                    {t('popup_guarantee')}
                  </span>
                </div>
              </div>

              <Button
                className="w-full bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold h-12"
                onClick={() => {
                  setShowPopup(false);
                  handleSubscribe();
                }}
              >
                {t('popup_cta')}
              </Button>

              <p className="text-center text-gray-400 text-xs mt-3">
                {t('popup_cancel')}
              </p>
            </div>
          </div>
        </div>
      )}

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Personal Alert Counter for non-subscribed users */}
          {!isSubscribed && alertsThisWeek > 0 && (
            <div className="mb-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{t('missed_appointments').replace('{count}', alertsThisWeek)}</p>
                    <p className="text-white/80 text-sm">
                      {t('missed_appointments_for').replace('{from}', preferences.residence_country || 'Tunisia').replace('{to}', preferences.destination_country || 'France')}
                    </p>
                  </div>
                </div>
                <Button
                  className="bg-white text-red-600 hover:bg-gray-100 font-bold"
                  onClick={handleSubscribe}
                >
                  {t('dont_miss_next')}
                </Button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]" data-testid="dashboard-title">
              {t('dashboard_title')}
            </h1>
            <p className="text-gray-600 mt-1">Welcome back, {user?.name}!</p>
          </div>

          {/* Appointment Map Section */}
          <div className="mb-10">
            <AppointmentMap isSubscribed={isSubscribed} />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Subscription & Preferences */}
            <div className="lg:col-span-2 space-y-6">
              {/* Subscription Status */}
              <Card className="rounded-sm border-0 shadow-sm" data-testid="subscription-card">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {t('dashboard_subscription')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isSubscribed ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A0A0A]">{t('dashboard_active')}</p>
                          <p className="text-sm text-gray-600">
                            {t('dashboard_expires')}: {user?.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-sm font-medium">
                        €7.99/month
                      </span>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] rounded-sm p-6 text-white">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#FF3B30] rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                          <Bell className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-bold text-xl mb-2">
                            {t('dashboard_never_miss_title')}
                          </h3>
                          <p className="text-gray-300 text-sm mb-4">
                            {interpolateBold(t('dashboard_never_miss_desc'), {
                              tunis: 'Tunis', sfax: 'Sfax', rabat: 'Rabat', casablanca: 'Casablanca', cairo: 'Cairo'
                            })}
                          </p>

                          <ul className="space-y-2 mb-4 text-sm">
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-400" />
                              <span>{t('dashboard_feature_realtime')}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-400" />
                              <span>{t('dashboard_feature_sms_email')}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-400" />
                              <span>{t('dashboard_feature_tracking')}</span>
                            </li>
                          </ul>

                          {/* Zwei klare Zahlungsoptionen nebeneinander: Stripe (Karte) und
                              DHMAD.tn (Escrow, TND) — immer beide sichtbar, unabhängig vom
                              Wohnsitzland, damit jeder Nutzer frei wählen kann. */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Button
                              className="h-12 rounded-sm bg-[#FF3B30] font-bold text-white shadow-lg hover:bg-[#E5352B]"
                              onClick={handleSubscribe}
                              disabled={checkoutLoading}
                              data-testid="subscribe-btn"
                            >
                              {checkoutLoading ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  {t('dashboard_processing')}
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  {t('dashboard_buy_stripe')} · €{t('pricing_price')}
                                </>
                              )}
                            </Button>

                            <Button
                              asChild
                              variant="outline"
                              className="h-12 rounded-sm border-2 border-[#FF3B30] font-bold text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid="dhmad-payment-link"
                            >
                              <a href={dhmadLink} target="_blank" rel="noopener noreferrer">
                                🇹🇳 {t('dashboard_buy_dhmad')} ({t('pricing_price_tnd')} TND)
                                <ExternalLink className="w-4 h-4 ml-2" />
                              </a>
                            </Button>
                          </div>

                          <p className="mt-3 text-xs text-gray-400">
                            {t('dashboard_cancel_anytime')} · {t('dashboard_dhmad_note')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Hotel-Reservierung für das Visa-Dossier (eigenes Produkt) */}
              <HotelBookingSection defaultDestination={preferences.destination_country} />

              {/* Notification Preferences */}
              <Card className="rounded-sm border-0 shadow-sm" data-testid="preferences-card">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    {t('dashboard_preferences')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isSubscribed && (
                    <div className="bg-gradient-to-r from-[#FF3B30]/10 to-orange-100 border border-[#FF3B30]/20 rounded-sm p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <Bell className="w-5 h-5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-heading font-semibold text-[#0A0A0A]">
                            Get Instant TLS Appointment Alerts!
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Subscribe now to receive real-time SMS & email notifications when new visa appointments become available at TLS Contact centers in <strong>Tunis</strong>, <strong>Sfax</strong>, <strong>Rabat</strong>, <strong>Casablanca</strong> and <strong>Cairo</strong>. Don't miss your chance to book!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Residence Country */}
                    <div className="space-y-2">
                      <Label>{t('dashboard_residence')}</Label>
                      <Select
                        value={preferences.residence_country}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, residence_country: value }))}
                        disabled={!isSubscribed}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="residence-country-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.from_countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Destination Country */}
                    <div className="space-y-2">
                      <Label>{t('dashboard_destination')}</Label>
                      <Select
                        value={preferences.destination_country}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, destination_country: value }))}
                        disabled={!isSubscribed}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="destination-country-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.to_countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Visa Center */}
                    <div className="space-y-2">
                      <Label>Visa Centre</Label>
                      <Select
                        value={preferences.visa_center}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, visa_center: value }))}
                        disabled={!isSubscribed}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="visa-center-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(countries.visa_centers || ['TLS', 'VFS Global', 'BLS']).map(center => (
                            <SelectItem key={center} value={center}>{center}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Notification Type */}
                    <div className="space-y-2">
                      <Label>{t('dashboard_notification_type')}</Label>
                      <Select
                        value={preferences.notification_type}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, notification_type: value }))}
                        disabled={!isSubscribed}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="notification-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              {t('email')}
                            </div>
                          </SelectItem>
                          <SelectItem value="sms">
                            <div className="flex items-center gap-2">
                              <Smartphone className="w-4 h-4" />
                              {t('sms')}
                            </div>
                          </SelectItem>
                          <SelectItem value="both">
                            <div className="flex items-center gap-2">
                              <Bell className="w-4 h-4" />
                              {t('both')}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Visa Type */}
                    <div className="space-y-2">
                      <Label>Visa Type</Label>
                      <Select
                        value={preferences.visa_type}
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, visa_type: value }))}
                        disabled={!isSubscribed}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="visa-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VISA_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {type.fr} / {type.en}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Phone Number with Country Code */}
                    <div className="space-y-2">
                      <Label>{t('dashboard_phone')}</Label>
                      <div className="flex gap-2">
                        <div className="w-24 flex items-center justify-center bg-gray-100 border border-gray-200 rounded-sm px-3 text-sm font-mono font-medium text-gray-700">
                          {getPhoneCode()}
                        </div>
                        <Input
                          type="tel"
                          placeholder="XX XXX XXXX"
                          value={preferences.phone_number?.replace(getPhoneCode(), '') || ''}
                          onChange={(e) => {
                            const number = e.target.value.replace(/[^0-9]/g, '');
                            setPreferences(prev => ({
                              ...prev,
                              phone_number: getPhoneCode() + number
                            }));
                          }}
                          className="rounded-sm flex-1"
                          disabled={!isSubscribed}
                          data-testid="phone-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Country code automatically set based on your residence country
                      </p>
                    </div>
                  </div>

                  <Button
                    className={`${isSubscribed
                      ? 'bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'} font-semibold rounded-sm`}
                    onClick={handleSavePreferences}
                    disabled={saving || !isSubscribed}
                    data-testid="save-preferences-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : isSubscribed ? (
                      t('dashboard_save')
                    ) : (
                      'Subscribe to Save Preferences'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Recent Alerts */}
            <div className="space-y-6">
              <Card className="rounded-sm border-0 shadow-sm" data-testid="recent-alerts-card">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">
                    {t('dashboard_recent')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appointments.length > 0 ? (
                    <div className="space-y-3">
                      {appointments.map((apt, idx) => (
                        <div
                          key={apt.id || idx}
                          className="p-3 bg-green-50 border border-green-200 rounded-sm"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-[#0A0A0A]">{apt.country_to}</p>
                              <p className="text-sm text-gray-600">{apt.location}</p>
                              {isSubscribed ? (
                                <p className="text-sm text-green-600 font-medium">{apt.date}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">{t('dashboard_hidden_date')}</p>
                              )}
                            </div>
                            {isSubscribed && (
                              <a
                                href="https://visas-fr.tlscontact.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#FF3B30] hover:underline text-sm flex items-center gap-1"
                              >
                                Book <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">No recent alerts</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {isSubscribed
                          ? "We'll notify you when slots open"
                          : "Subscribe to get alerts"
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* LIVE ALERTS FEED - Real-time notifications showcase */}
              <Card className="rounded-sm border-0 shadow-lg overflow-hidden relative" data-testid="live-alerts-feed">
                {/* Animated Header */}
                <div className="bg-gradient-to-r from-[#FF3B30] via-[#FF6B5B] to-[#FF3B30] p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Activity className="w-6 h-6 text-white" />
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping" />
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-white text-lg">Live Alert Feed</h3>
                        <p className="text-white/80 text-xs">Real-time visa appointment notifications</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-white text-xs font-medium">LIVE</span>
                    </div>
                  </div>
                </div>

                <CardContent className="p-0">
                  {/* Live Alerts List - Dynamic Mock Data */}
                  <div className="divide-y divide-gray-100">
                    {liveAlerts.map((alert, index) => (
                      <div key={index} className={`p-4 relative group hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-green-50/50 hover:bg-green-50' : ''}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full ${index === 0 ? 'bg-green-500' : alert.color} flex items-center justify-center flex-shrink-0`}>
                            {index === 0 ? <Zap className="w-5 h-5 text-white" /> :
                             index === 1 ? <Bell className="w-5 h-5 text-white" /> :
                             index === 2 ? <Sparkles className="w-5 h-5 text-white" /> :
                             <TrendingUp className="w-5 h-5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {alert.time.badge && (
                                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-medium">NEW</span>
                              )}
                              <span className="text-xs text-gray-400">{alert.time.badge ? `${alert.time.mins} min ago` : alert.time.text}</span>
                            </div>
                            {isSubscribed ? (
                              <>
                                <p className="font-semibold text-[#0A0A0A]">{alert.country} Visa - {alert.city}</p>
                                <p className="text-sm text-gray-600">{alert.slots} slots available for {alert.date}</p>
                                {index === 0 && (
                                  <a href="#" className="inline-flex items-center gap-1 text-sm text-[#FF3B30] font-medium mt-2 hover:underline">
                                    Book Now <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <p className={`font-semibold ${index === 0 ? 'text-[#0A0A0A]' : 'text-gray-300'} blur-sm select-none`}>{alert.country} Visa - {alert.city}</p>
                                  <Lock className="w-4 h-4 text-gray-400" />
                                </div>
                                <p className={`text-sm ${index === 0 ? 'text-gray-400' : 'text-gray-300'} blur-sm select-none`}>{alert.slots} slots available for {alert.date}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Upgrade CTA for non-subscribers */}
                  {!isSubscribed && (
                    <div className="relative">
                      <div className="absolute inset-x-0 -top-20 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />

                      <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] p-6 text-center">
                        <div className="max-w-sm mx-auto">
                          <div className="w-16 h-16 bg-[#FF3B30]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <EyeOff className="w-8 h-8 text-[#FF3B30]" />
                          </div>
                          <h4 className="font-heading font-bold text-white text-xl mb-2">
                            You're Missing Live Alerts!
                          </h4>
                          <p className="text-gray-400 text-sm mb-4">
                            <span className="text-[#FF3B30] font-semibold">17 appointments</span> were booked in the last hour.
                            Unlock full access to see exact locations, dates & book instantly.
                          </p>
                          <div className="flex flex-col gap-3">
                            <Button
                              className="w-full bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold rounded-sm h-12"
                              onClick={handleSubscribe}
                              disabled={checkoutLoading}
                            >
                              {checkoutLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <>
                                  <Eye className="w-5 h-5 mr-2" />
                                  Unlock All Alerts - €7.99/month
                                </>
                              )}
                            </Button>
                            <p className="text-gray-500 text-xs flex items-center justify-center gap-2">
                              <Shield className="w-3 h-3" />
                              Cancel anytime • Instant access • SMS + Email
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stats Bar for subscribers */}
                  {isSubscribed && (
                    <div className="bg-gray-50 p-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-500">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Last updated: Just now
                          </span>
                        </div>
                        <span className="text-green-600 font-medium">
                          <TrendingUp className="w-4 h-4 inline mr-1" />
                          {alertsToday} alerts today
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="rounded-sm border-0 shadow-sm bg-[#0A0A0A] text-white">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold mb-4">Monitoring Status</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Residence</span>
                      <span className="font-medium">{preferences.residence_country}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Destination</span>
                      <span className="font-medium">{preferences.destination_country}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Visa Type</span>
                      <span className="font-medium">
                        {VISA_TYPES.find(t => t.value === preferences.visa_type)?.en || preferences.visa_type}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Alert Type</span>
                      <span className="font-medium capitalize">{preferences.notification_type}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                      <span className="text-gray-400">Status</span>
                      {isSubscribed ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                          Active
                        </span>
                      ) : (
                        <span className="text-[#FF3B30]">Inactive</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Security Notice */}
              <Card className="rounded-sm border-0 shadow-sm bg-green-50 border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-800 text-sm">Security Notice</p>
                      <p className="text-green-700 text-xs mt-1">
                        {t('dashboard_security_notice')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget page="dashboard" />
    </div>
  );
};

export default Dashboard;